import { ArrowDown, ArrowUp, ChevronsUpDown, Filter } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import { alignmentClass, type Alignment } from "./fields"
import type { ColumnFilter, DateRangeValue, SortDirection } from "./types"

export type FilterValue = string[] | DateRangeValue

type ColumnHeaderProps = {
  label: string
  alignment: Alignment
  sort?: {
    direction: SortDirection | null
    onToggle: () => void
  }
  filter?: {
    definition: ColumnFilter
    value?: FilterValue
    onChange: (value: FilterValue | null) => void
  }
}

/**
 * Sorting and filtering cannot share the header's click, so they do not: the
 * label toggles the order and a separate glyph opens the filter. Sorting is the
 * more frequent action of the two and takes the larger target — the same split
 * Linear, Airtable and Notion arrived at.
 *
 * A column with no `sortKey` renders no button at all rather than a disabled
 * one. The server's whitelist decides what can be ordered, and an affordance
 * that is always dead is worse than no affordance.
 */
export function ColumnHeader({ label, alignment, sort, filter }: ColumnHeaderProps) {
  const active = isFilterActive(filter?.value)

  return (
    <div
      className={cn(
        "flex items-center gap-1",
        alignment === "right" && "justify-end",
        alignmentClass(alignment)
      )}
    >
      {sort ? (
        <button
          type="button"
          onClick={sort.onToggle}
          data-testid={`sort-${label}`}
          className={cn(
            "-mx-1 inline-flex items-center gap-1 rounded-md px-1 py-0.5 font-medium",
            "hover:text-foreground focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
            sort.direction ? "text-foreground" : "text-muted-foreground"
          )}
          aria-label={
            sort.direction === "asc"
              ? t("ui.react.components.ui.data_table.sort_descending")
              : t("ui.react.components.ui.data_table.sort_ascending")
          }
        >
          {label}
          <SortGlyph direction={sort.direction} />
        </button>
      ) : (
        <span className="font-medium">{label}</span>
      )}

      {filter ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="relative shrink-0"
              data-testid={`filter-${label}`}
              aria-label={t("ui.react.components.ui.data_table.filter_column", { column: label })}
            >
              <Filter className={cn("size-4", active ? "text-foreground" : "text-muted-foreground")} />
              {active ? (
                <span
                  aria-hidden="true"
                  className="bg-primary absolute top-1 right-1 size-1.5 rounded-full"
                />
              ) : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={alignment === "right" ? "end" : "start"} className="w-56">
            {filter.definition.kind === "enum" ? (
              <EnumFilter
                options={filter.definition.options}
                value={Array.isArray(filter.value) ? filter.value : []}
                multiple={filter.definition.multiple ?? true}
                onChange={filter.onChange}
              />
            ) : (
              <DateRangeFilter
                value={isDateRange(filter.value) ? filter.value : { from: null, to: null }}
                onChange={filter.onChange}
              />
            )}
            {active ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => filter.onChange(null)}>
                  {t("ui.react.components.ui.data_table.clear_filter")}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  )
}

function SortGlyph({ direction }: { direction: SortDirection | null }) {
  if (direction === "asc") return <ArrowUp className="size-3.5" aria-hidden="true" />
  if (direction === "desc") return <ArrowDown className="size-3.5" aria-hidden="true" />

  return <ChevronsUpDown className="size-3.5 opacity-50" aria-hidden="true" />
}

function EnumFilter({
  options,
  value,
  multiple,
  onChange,
}: {
  options: { value: string; label: string }[]
  value: string[]
  multiple: boolean
  onChange: (value: FilterValue | null) => void
}) {
  const toggle = (option: string, checked: boolean) => {
    if (!multiple) return onChange(checked ? [ option ] : null)

    const next = checked ? [ ...value, option ] : value.filter((entry) => entry !== option)
    onChange(next.length ? next : null)
  }

  return (
    <>
      {options.map((option) => (
        <DropdownMenuCheckboxItem
          key={option.value}
          checked={value.includes(option.value)}
          // Radix closes on select by default. A checkbox list whose menu shuts
          // after the first tick makes ticking a second one a second trip.
          onSelect={(event) => event.preventDefault()}
          onCheckedChange={(checked) => toggle(option.value, Boolean(checked))}
          data-testid={`filter-option-${option.value}`}
        >
          {option.label}
        </DropdownMenuCheckboxItem>
      ))}
    </>
  )
}

function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateRangeValue
  onChange: (value: FilterValue | null) => void
}) {
  const update = (bound: "from" | "to", next: string) => {
    const merged = { ...value, [bound]: next || null }
    onChange(merged.from || merged.to ? merged : null)
  }

  return (
    // Radix menus swallow keystrokes for typeahead, which would eat every
    // character typed into these fields. The menu never sees them.
    <div
      className="space-y-2 p-2"
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="space-y-1">
        <Label htmlFor="filter-date-from" className="text-xs">
          {t("ui.react.components.ui.data_table.date_from")}
        </Label>
        <Input
          id="filter-date-from"
          type="date"
          value={value.from ?? ""}
          onChange={(event) => update("from", event.target.value)}
          data-testid="filter-date-from"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="filter-date-to" className="text-xs">
          {t("ui.react.components.ui.data_table.date_to")}
        </Label>
        <Input
          id="filter-date-to"
          type="date"
          value={value.to ?? ""}
          onChange={(event) => update("to", event.target.value)}
          data-testid="filter-date-to"
        />
      </div>
    </div>
  )
}

function isDateRange(value: FilterValue | undefined): value is DateRangeValue {
  return Boolean(value) && !Array.isArray(value)
}

export function isFilterActive(value: FilterValue | undefined): boolean {
  if (!value) return false
  if (Array.isArray(value)) return value.length > 0

  return Boolean(value.from || value.to)
}
